import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/blog/ProtectedRoute";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail } from "lucide-react";
import { format } from "date-fns";

interface Inquiry {
  id: string;
  name: string;
  email: string;
  message: string;
  created_at: string;
  post_id: string | null;
  blog_posts: {
    title: string;
    slug: string;
  } | null;
}

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("blog_contact_inquiries")
      .select(`
        *,
        blog_posts (title, slug)
      `)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setInquiries(data);
    }
    setIsLoading(false);
  };

  return (
    <ProtectedRoute>
      <AdminLayout>
        <div>
          <h1 className="text-3xl font-bold mb-8">Contact Inquiries</h1>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : inquiries.length === 0 ? (
            <Card>
              <CardContent className="py-20 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No inquiries yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {inquiries.map((inquiry) => (
                <Card key={inquiry.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg mb-2">{inquiry.name}</CardTitle>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                          <a
                            href={`mailto:${inquiry.email}`}
                            className="hover:text-foreground transition-colors"
                          >
                            {inquiry.email}
                          </a>
                          <span>{format(new Date(inquiry.created_at), "MMM d, yyyy 'at' h:mm a")}</span>
                        </div>
                        {inquiry.blog_posts && (
                          <Badge variant="secondary">
                            Re: {inquiry.blog_posts.title}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground whitespace-pre-wrap">{inquiry.message}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    </ProtectedRoute>
  );
}
